// FieldDisplayFieldsField.jsx

import React, { useMemo } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';

// Utilities
import { getRelationDisplayFieldOptions } from 'utilities/RelationDisplayFields';

// Components
import CheckboxList from 'components/checkboxlist/CheckboxList';
import ModalField from 'components/modal/field/ModalField';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';

/**
 * FieldDisplayFieldsField
 *
 * Checkbox list of target-type fields to show as muted metadata under
 * Relation titles. Nothing selected by default.
 *
 * @param {string} targetType - Relation target type key.
 * @param {string[]} displayFields - Selected field names.
 * @param {function} onDisplayFieldsChange - Called with the next name list.
 *
 * @returns {ReactNode} The rendered control.
 */
const FieldDisplayFieldsField = ({
  targetType,
  displayFields = [],
  onDisplayFieldsChange,
}) => {

  // ----- CONTEXT ----- //

  const { getType } = useNodeTypes();

  // ----- MEMOS ----- //

  const resolvedTargetType = String(targetType || '').trim();

  const options = useMemo(() => {
    if (!resolvedTargetType) return [];
    const typeDef = getType(resolvedTargetType);
    const fields = Array.isArray(typeDef?.schema?.fields) ? typeDef.schema.fields : [];
    return getRelationDisplayFieldOptions(fields);
  }, [getType, resolvedTargetType]);

  const selectedValues = Array.isArray(displayFields) ? displayFields : [];

  // ----- RENDER ----- //

  if (!resolvedTargetType) return null;

  return (
    <ModalField>
      <ModalLabel label="Display Fields" />
      <ModalHint hint="Optional. Checked fields from the related record show as muted lines under the title." />

      {options.length === 0 ? (
        <div className="field-modal-empty">No displayable fields on this database yet.</div>
      ) : (
        <CheckboxList
          multiSelect
          options={options}
          values={selectedValues}
          onChange={(nextValues) => {
            onDisplayFieldsChange?.(Array.isArray(nextValues) ? nextValues : []);
          }}
        />
      )}
    </ModalField>
  );
};

// Dev Tools
FieldDisplayFieldsField.displayName = 'FieldDisplayFieldsField';

export default FieldDisplayFieldsField;
